import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FunFact } from "../noc/FunFact";
import axios from "axios";
import { DateTime } from "luxon";

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import LottieAnimation from "../../animation/LottieAnimation";

const AcceptInstant = () => {
  const navigate = useNavigate();
  const sessions = useSelector((state) => state.sessions);
  const location = useLocation();
  const theme = useTheme();
  const queryParams = new URLSearchParams(location.search);

  const notaryIDQuery = queryParams.get("ni");
  const instantTokenQuery = queryParams.get("it");
  const taskIdQuery = queryParams.get("ti");
  const signeeTokenQuery = queryParams.get("st");
  const reasonQuery = queryParams.get("reason");

  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const [pageLoading, setPageLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(null);
  const [errorMessage, setErrorMessage] = useState("Please Wait");
  const [errorType, setErrorType] = useState("waiting");
  const [isLoading, setIsLoading] = useState(true);
  const [funData, setFunData] = useState([]);
  const [appointment, setAppointment] = useState(null);
  const [session_url, setSession_url] = useState(null);
  const [timezone] = useState("America/New_York");
  const [sessionReady, setSessionReady] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const hasValidated = useRef(false);

  useEffect(() => {
    if (isSuccess === true) {
      const timert = setTimeout(() => {
        navigate("/login");
      }, 300000);

      return () => clearTimeout(timert);
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    let funCount = 0;
    const getFunFacts = async () => {
      const resp = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/funFacts`
      );

      setFunData(resp.data.data.funFacts);
    };
    if (funCount < 1) {
      getFunFacts();
      funCount += 1;
    }
  }, []);

  useEffect(() => {
    if (
      !instantTokenQuery ||
      !notaryIDQuery ||
      !taskIdQuery ||
      !signeeTokenQuery ||
      !reasonQuery
    ) {
      navigate("/");
      return;
    }

    const validate = async () => {
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/check-finalize-state`,
          {
            instant_token: instantTokenQuery,
            notary_id: notaryIDQuery,
            task_id: taskIdQuery,
            reason: reasonQuery,
          },
          {
            withCredentials: true,
          }
        );

        if (resp?.data?.type === "process") {
          // Immediately attempt to accept the job
          const tokenResponse = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/notary/accept-instant`,
            {
              instant_token: instantTokenQuery,
              notary_id: notaryIDQuery,
              task_id: taskIdQuery,
              signee_token: signeeTokenQuery,
              reason: reasonQuery,
            },
            {
              withCredentials: true,
            }
          );

          // Handle immediate response
          if (tokenResponse.data.success === true) {
            // Check if this is the final success or still processing
            if (tokenResponse.data.type === "accepted") {
              // Final acceptance - documents are ready
              setIsSuccess(true);
              setIsLoading(false);
              setErrorMessage(tokenResponse?.data?.message);
              setErrorType(tokenResponse?.data?.type);
              setSession_url(tokenResponse?.data?.session_url);

              if (tokenResponse?.data?.appointment) {
                const dateTime = DateTime.fromISO(
                  tokenResponse?.data?.appointment,
                  {
                    zone: timezone,
                  }
                );

                setAppointment(
                  dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a")
                );

                const appointmentTimeUTC = DateTime.fromISO(
                  tokenResponse?.data?.appointment,
                  {
                    zone: "UTC",
                  }
                );
                // Get the current time in UTC
                const currentTimeUTC = DateTime.now().setZone("UTC");
                const difference = appointmentTimeUTC.diff(
                  currentTimeUTC,
                  "minutes"
                ).minutes;

                // Session is ready if:
                // 1. We're within 10 minutes before the session starts (difference > 0 and <= 10)
                // 2. OR the session time has passed (difference < 0, meaning session is active)
                if ((difference > 0 && difference <= 10) || difference < 0) {
                  setSessionReady(true);
                } else {
                  setSessionReady(false);
                }
              }
            } else {
              // Still processing - wait for websocket notification
              setErrorMessage("Processing your acceptance...");
              setErrorType("processing");
              setIsLoading(true);
              // Keep waiting for websocket notification
            }
          } else if (tokenResponse.data.success === false) {
            // Handle initial "taken" error - but keep WebSocket open for potential success
            if (tokenResponse?.data?.type === "taken") {
              setErrorMessage("Checking acceptance status...");
              setErrorType("processing");
              setIsLoading(true);
              // Set up WebSocket for potential success message
            } else {
              setErrorMessage(tokenResponse?.data?.message);
              setErrorType(tokenResponse?.data?.type);
              setIsSuccess(false);
              setIsLoading(false);
              return; // Don't set up WebSocket for other errors
            }
          } else {
            // Processing state - wait for WebSocket notification
            setErrorMessage("Processing your acceptance...");
            setErrorType("processing");
            setIsLoading(true);
          }

          // Set up WebSocket connection for processing states and potential "taken" recoveries
          // Establish temporary WebSocket connection for real-time updates (only once)
          let wsRef = null;

          const establishAcceptanceWebSocket = () => {
            const wsUrl = `${process.env.REACT_APP_WS_URL}/nocApi_ws/notary_acceptance/${instantTokenQuery}/${notaryIDQuery}`;

            try {
              const ws = new WebSocket(wsUrl);
              wsRef = ws;

              ws.onopen = () => {
                // WebSocket connected
              };

              ws.onmessage = (event) => {
                try {
                  const message = JSON.parse(event.data);

                  if (message.event === "instant_ron_accepted") {
                    const data = message.data;
                    // Processing completed successfully!
                    setIsSuccess(true);
                    setIsLoading(false);
                    setErrorMessage(
                      data.message ||
                        "Document processing completed successfully! You can now join the notarization session."
                    );
                    setErrorType("accepted");
                    setSession_url(data.session_url);

                    if (data.appointment) {
                      const dateTime = DateTime.fromISO(data.appointment, {
                        zone: timezone,
                      });
                      setAppointment(
                        dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a")
                      );

                      const appointmentTimeUTC = DateTime.fromISO(
                        data.appointment,
                        {
                          zone: "UTC",
                        }
                      );
                      const currentTimeUTC = DateTime.now().setZone("UTC");
                      const difference = appointmentTimeUTC.diff(
                        currentTimeUTC,
                        "minutes"
                      ).minutes;

                      if (
                        (difference > 0 && difference <= 10) ||
                        difference < 0
                      ) {
                        setSessionReady(true);
                      } else {
                        setSessionReady(false);
                      }
                    }

                    // Close WebSocket after successful completion
                    ws.close();
                  } else if (message.event === "connection_established") {
                    // WebSocket connection confirmed
                  }
                } catch (error) {
                  console.error("Error parsing WebSocket message:", error);
                }
              };

              ws.onerror = (error) => {
                console.error("WebSocket error:", error);
              };

              ws.onclose = () => {
                wsRef = null;
              };

              return ws;
            } catch (error) {
              console.error("Failed to establish WebSocket:", error);
              return null;
            }
          };

          establishAcceptanceWebSocket();

          // Cleanup function
          const cleanup = () => {
            if (wsRef && wsRef.readyState === WebSocket.OPEN) {
              wsRef.close();
            }
          };

          // Cleanup when leaving page
          window.addEventListener("beforeunload", cleanup);

          return () => {
            cleanup();
            window.removeEventListener("beforeunload", cleanup);
          };
        } else {
          setErrorMessage(resp?.data?.message);
          setErrorType(resp?.data?.type);

          if (resp?.data?.type === "accepted") {
            setIsSuccess(true);
            // Session was already accepted - use returned session details for recovery
            if (resp?.data?.session_url) {
              setSession_url(resp.data.session_url);
            }
            if (resp?.data?.appointment && resp.data.appointment.length > 0) {
              const appointmentRaw = resp.data.appointment[0];
              const dateTime = DateTime.fromObject(
                {
                  year: appointmentRaw.year,
                  month: appointmentRaw.month,
                  day: appointmentRaw.day_of_month,
                  hour: parseInt(appointmentRaw.time.split(":")[0]),
                  minute: parseInt(appointmentRaw.time.split(":")[1]),
                },
                { zone: "UTC" }
              ).setZone(timezone);

              setAppointment(dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a"));

              // Check if session is ready (within 10 minutes or past start time)
              const currentTimeUTC = DateTime.now().setZone("UTC");
              const appointmentTimeUTC = DateTime.fromObject(
                {
                  year: appointmentRaw.year,
                  month: appointmentRaw.month,
                  day: appointmentRaw.day_of_month,
                  hour: parseInt(appointmentRaw.time.split(":")[0]),
                  minute: parseInt(appointmentRaw.time.split(":")[1]),
                },
                { zone: "UTC" }
              );
              const difference = appointmentTimeUTC.diff(
                currentTimeUTC,
                "minutes"
              ).minutes;

              if ((difference > 0 && difference <= 10) || difference < 0) {
                setSessionReady(true);
              }
            }
          } else {
            setIsSuccess(false);
          }
          setIsLoading(false);
        }
        setPageLoading(false);
      } catch (error) {
        setErrorMessage(error?.response?.data?.message);
        setErrorType(error?.response?.data?.type);
        if (error?.response?.data?.type === "unauthorized") {
          navigate("/login");
        }
        setPageLoading(false);
        setIsLoading(false);
      }
    };

    if (!hasValidated.current) {
      hasValidated.current = true;
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    notaryIDQuery,
    instantTokenQuery,
    taskIdQuery,
    signeeTokenQuery,
    reasonQuery,
    timezone,
  ]);
  // Note: We're using a temporary WebSocket connection for acceptance
  // No need for regular notary session WebSocket since the notary isn't logged in

  // Listen for acceptance status updates in Redux state
  // This will be triggered by websocket messages processed through the Redux middleware
  const notarySession = useSelector((state) => state.notarySession);

  useEffect(() => {
    // Handle real-time acceptance updates from websocket
    if (notarySession?.lastWebSocketMessage) {
      const message = notarySession.lastWebSocketMessage;

      if (message.event === "instant_ron_processing") {
        setErrorMessage("Processing your acceptance...");
        setErrorType("processing");
        setIsLoading(true);
      } else if (message.event === "instant_ron_accepted") {
        const responseData = message.data;
        setIsSuccess(true);
        setIsLoading(false);
        setErrorMessage(
          responseData.message || "Job has been accepted successfully"
        );
        setErrorType(responseData.type || "accepted");

        if (responseData.session_url) {
          setSession_url(responseData.session_url);
        }

        if (responseData.appointment) {
          const dateTime = DateTime.fromISO(responseData.appointment, {
            zone: timezone,
          });
          setAppointment(dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a"));

          const appointmentTimeUTC = DateTime.fromISO(
            responseData.appointment,
            {
              zone: "UTC",
            }
          );
          const currentTimeUTC = DateTime.now().setZone("UTC");
          const difference = appointmentTimeUTC.diff(
            currentTimeUTC,
            "minutes"
          ).minutes;

          if ((difference > 0 && difference <= 10) || difference < 0) {
            setSessionReady(true);
          } else {
            setSessionReady(false);
          }
        }
      }
    }
  }, [notarySession?.lastWebSocketMessage, timezone]);

  // Handle connection recovery
  useEffect(() => {
    const handleConnectionRecovery = async () => {
      if (
        wsConnected &&
        sessions?.notarySession?.wsConnected &&
        instantTokenQuery &&
        taskIdQuery
      ) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/notary/recover-acceptance-state`,
            {
              instant_token: instantTokenQuery,
              task_id: taskIdQuery,
              user_id: sessions?.notarySession?.user_id || "",
              session_type: "notarySession",
            },
            {
              withCredentials: true,
            }
          );

          if (response.data.success && response.data.recovery_data) {
            const recoveryData = response.data.recovery_data;

            // Restore state from recovery data
            if (recoveryData.current_state) {
              const state = recoveryData.current_state;

              if (state.status === "accepted") {
                setIsSuccess(true);
                setIsLoading(false);
                setErrorMessage(
                  state.message || "Job has been accepted successfully"
                );
                setErrorType("accepted");

                if (state.session_url) {
                  setSession_url(state.session_url);
                }

                if (state.appointment) {
                  const dateTime = DateTime.fromISO(state.appointment, {
                    zone: timezone,
                  });
                  setAppointment(
                    dateTime.toFormat("cccc, LLLL dd, yyyy hh:mm a")
                  );

                  const appointmentTimeUTC = DateTime.fromISO(
                    state.appointment,
                    {
                      zone: "UTC",
                    }
                  );
                  const currentTimeUTC = DateTime.now().setZone("UTC");
                  const difference = appointmentTimeUTC.diff(
                    currentTimeUTC,
                    "minutes"
                  ).minutes;

                  // Session is ready if:
                  // 1. We're within 10 minutes before the session starts (difference > 0 and <= 10)
                  // 2. OR the session time has passed (difference < 0, meaning session is active)
                  if ((difference > 0 && difference <= 10) || difference < 0) {
                    setSessionReady(true);
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error("Failed to recover connection state:", error);
        }
      }
    };

    // Only attempt recovery if WebSocket is connected
    if (wsConnected && sessions?.notarySession?.wsConnected) {
      handleConnectionRecovery();
    }
  }, [
    wsConnected,
    sessions?.notarySession?.wsConnected,
    sessions?.notarySession?.user_id,
    instantTokenQuery,
    taskIdQuery,
    timezone,
  ]);

  // WebSocket handling is implemented through Redux middleware
  // The actual WebSocket messages are processed by:
  // 1. WebSocket middleware receives "instant_ron_accepted" events
  // 2. Calls guestSessionOnMessage() or userSessionOnMessage()
  // 3. Dispatches setInstantRonAccepted() Redux action
  // 4. Updates Redux state which triggers component re-renders
  // 5. This component uses the timeout recovery mechanism as fallback

  return (
    <>
      {pageLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            {tabletSize ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <img
                    style={{
                      width: "10rem",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/website_img/PermitRockstar.png"
                  />
                </Box>
                <LottieAnimation />
              </>
            ) : (
              <CircularProgress color="warning" size="10rem" />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={
              mobileSize
                ? {
                    justifyContent: "center",
                    overflow: "hidden",
                    width: "100%",
                    height: "100vh",
                  }
                : {
                    backgroundImage: "url('/img/website_img/homeImg.jpeg')",
                    backgroundRepeat: "repeat",
                    width: "100%",
                    flex: "1 1 auto",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    height: "100vh",
                  }
            }
          >
            <Box
              sx={
                tabletSize
                  ? {
                      display: "flex",
                      alignItems: "center",
                      flex: "1 1 auto",
                      justifyContent: "center",
                      backgroundColor: "#fff",
                      py: "10px",
                      width: "100%",
                    }
                  : {
                      backgroundColor: "#fff",
                      maxWidth: "60%",
                      maxHeight: "90%",
                      // minHeight: "80%",
                      borderRadius: "25px",
                      px: 3,
                      py: "10px",
                      width: "100%",
                      pt: "2rem",
                    }
              }
            >
              <div
                style={
                  tabletSize
                    ? { width: "100%", padding: "1rem", position: "relative" }
                    : { paddingBottom: "1rem", position: "relative" }
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() => {
                    navigate("/");
                  }}
                >
                  <img
                    style={{
                      maxWidth: mobileSize
                        ? "90%"
                        : tabletSize
                          ? "50%"
                          : "10rem",
                      height: "auto",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/website_img/PermitRockstar.png"
                  />
                </Box>
                {isSuccess && isSuccess === true ? (
                  <>
                    <Box>
                      <Typography
                        color="#4CAF50"
                        variant="h5"
                        sx={{ mb: 2, mt: 1 }}
                        align="center"
                      >
                        {errorMessage ||
                          "Documents have been validated and the appointment for the RON has been confirmed."}
                      </Typography>

                      {appointment && (
                        <>
                          <Typography
                            color="primary"
                            variant="h5"
                            fontWeight="bold"
                            align="center"
                            sx={{ mb: 1 }}
                          >
                            Session Starts at:
                          </Typography>
                          <Typography
                            color="primary"
                            variant="h4"
                            fontWeight="bold"
                            align="center"
                            sx={{ mb: 3 }}
                          >
                            {appointment}
                          </Typography>
                        </>
                      )}

                      <Grid container justifyContent="center">
                        <Grid item xs={12}>
                          <Button
                            size="large"
                            onClick={() => {
                              if (sessionReady) {
                                window.open(session_url, "_blank");
                              }
                              navigate("/login");
                            }}
                            fullWidth
                            variant="contained"
                          >
                            {sessionReady ? "Join Session" : "Back to Login"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                ) : errorType === "waiting" ? (
                  <>
                    <Box>
                      <Typography
                        color="primary"
                        variant="h5"
                        sx={{ mb: 2, mt: 1 }}
                        align="center"
                      >
                        Accepting Remote Online Notarization (RON) Appointment!
                      </Typography>
                      <Typography
                        color="primary"
                        variant="h5"
                        sx={{ mb: 2, mt: 1 }}
                        align="center"
                      >
                        {errorMessage}
                      </Typography>
                      <Typography
                        color="primary"
                        variant="body1"
                        align="center"
                        sx={{ mb: 3 }}
                      >
                        We are ensuring the document/s can be notarized to
                        ensure a smooth experience. Once we validate the
                        documents, you will be assigned the job and can start
                        the session.
                      </Typography>
                      <Typography
                        color="primary"
                        variant="body1"
                        align="center"
                        sx={{ mb: 3, fontWeight: "bold" }}
                      >
                        In the meantime, did you know...
                      </Typography>
                      <FunFact type="accept" data={funData} />
                      <Grid container justifyContent="center" mt={3}>
                        <Grid item xs={12}>
                          <LoadingButton
                            loading={true}
                            fullWidth
                            variant="contained"
                          >
                            Please Wait
                          </LoadingButton>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box>
                      <Typography
                        color={errorType === "processing" ? "primary" : "error"}
                        variant="h5"
                        sx={{ mb: 2, mt: 1 }}
                        align="center"
                      >
                        {errorType === "processing"
                          ? "Processing RON Appointment Acceptance"
                          : "Remote Online Notarization (RON) Appointment is Unavailable!"}
                      </Typography>
                      <Typography
                        color="primary"
                        variant="h5"
                        sx={{ mb: 2, mt: 1 }}
                        align="center"
                      >
                        {errorMessage}
                      </Typography>
                      <Typography
                        color="primary"
                        variant="body1"
                        align="center"
                        sx={{ mb: 3 }}
                      >
                        {errorType === "processing" ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <CircularProgress size={40} color="primary" />
                            <span>
                              Please wait while we process your acceptance and
                              prepare the notarization documents. This may take
                              a few moments.
                            </span>
                          </Box>
                        ) : (
                          `We regret to inform you ${
                            errorType === "taken"
                              ? "that the Remote Online Notarization (RON) appointment you selected has already been accepted by you or another notary."
                              : errorType === "not_found" &&
                                  errorMessage ===
                                    "You can not accept this job this time. The RON session is expired or does not exist"
                                ? "that the RON session you trying to accept is expired or does not exist."
                                : errorType === "not_found"
                                  ? "that we were unable to find and validate your notary account."
                                  : errorMessage ===
                                      "You can not accept this job this time. Failed to start RON session"
                                    ? "that we encountered an issue during the process of processing and initiating the RON session for the signee."
                                    : errorMessage ===
                                        "You can not accept this job this time. Job has been cancelled or doesn't exist"
                                      ? "that the RON session has been cancelled or doesn't exist."
                                      : "that there was an issue processing your acceptance. Please try again or contact support."
                          } We appreciate your prompt response and encourage you to log into your dashboard for other available notarization opportunities.`
                        )}
                      </Typography>
                      {errorType !== "processing" && (
                        <Grid container justifyContent="center">
                          <Grid item xs={12}>
                            <Button
                              onClick={() => {
                                navigate("/");
                              }}
                              fullWidth
                              variant="contained"
                            >
                              BACK TO MAIN MENU
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </>
                )}
              </div>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default AcceptInstant;
